import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [params] = useSearchParams();
  const resetToken = params.get("token");
  const [mode, setMode] = useState(null); // 'login' | 'bootstrap' | 'forgot' | 'reset'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resetToken) { setMode("reset"); return; }
    api.get("/admin/exists").then((r) => setMode(r.data.exists ? "login" : "bootstrap")).catch(() => setMode("login"));
  }, [resetToken]);

  const errMsg = (err, fb) => {
    const d = err?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : fb);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/admin/forgot-password", { email });
        toast.success(data.message || "Email envoyé si le compte existe.");
        setMode("login");
      } else if (mode === "reset") {
        await api.post("/admin/reset-password", { token: resetToken, password });
        toast.success("Mot de passe mis à jour. Connecte-toi.");
        navigate("/admin", { replace: true });
        setMode("login");
        setPassword("");
      } else {
        const path = mode === "bootstrap" ? "/admin/bootstrap" : "/admin/login";
        const { data } = await api.post(path, { email, password, name });
        setUser(data);
        toast.success(mode === "bootstrap" ? "Compte admin créé" : "Connecté");
        navigate("/coach", { replace: true });
      }
    } catch (err) {
      errMsg(err, "Une erreur est survenue");
    } finally { setBusy(false); }
  };

  if (!mode) return <div className="min-h-screen bg-obsidian flex items-center justify-center"><Loader2 className="animate-spin text-sage" /></div>;

  const subtitle = {
    bootstrap: "Créer le compte admin principal",
    login: "Connexion réservée à l'équipe",
    forgot: "Réinitialiser ton mot de passe",
    reset: "Choisis un nouveau mot de passe",
  }[mode];

  const cta = {
    bootstrap: "Créer l'administrateur",
    login: "Se connecter",
    forgot: "Envoyer le lien",
    reset: "Enregistrer le mot de passe",
  }[mode];

  return (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center px-5">
      <div className="w-full max-w-sm" data-testid="admin-page">
        <div className="flex items-center gap-2.5 mb-6">
          <span className="w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center"><ShieldCheck size={20} className="text-sage" /></span>
          <div>
            <div className="font-display text-lg font-medium">Espace administrateur</div>
            <div className="text-xs text-white/50">{subtitle}</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "bootstrap" && (
            <input data-testid="admin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom (optionnel)"
              className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          )}
          {(mode === "login" || mode === "bootstrap" || mode === "forgot") && (
            <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          )}
          {(mode === "login" || mode === "bootstrap" || mode === "reset") && (
            <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "reset" ? "Nouveau mot de passe (min. 6)" : "Mot de passe"}
              className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          )}
          <button data-testid="admin-submit" type="submit" disabled={busy}
            className="w-full rounded-full bg-sage text-obsidian py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {cta}
          </button>
        </form>
        <div className="mt-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="text-xs text-white/40 hover:text-white/70">← Retour</button>
          {mode === "login" && (
            <button data-testid="admin-forgot-link" onClick={() => setMode("forgot")} className="text-xs text-sage hover:underline">Mot de passe oublié ?</button>
          )}
          {mode === "forgot" && (
            <button onClick={() => setMode("login")} className="text-xs text-sage hover:underline">Retour à la connexion</button>
          )}
        </div>
      </div>
    </div>
  );
}
